/**
 * VisitaForm — SlideOver con formulario para registrar/editar una visita.
 *
 * Botón "Capturar ubicación" pide GPS al navegador (opcional).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, TextField, MenuItem, Stack, Chip, Typography,
  Autocomplete, CircularProgress, IconButton,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import SlideOver from '../common/SlideOver';
import {
  API_BASE, getEmpresaId, getAuthHeaders,
  CANALES_VISITA, RESULTADOS_VISITA,
} from './format';

export default function VisitaForm({ open, onClose, onSaved, prefill }) {
  const [target, setTarget] = useState(prefill?.agencia_id ? 'agencia' : 'prospecto');
  const [agencias, setAgencias] = useState([]);
  const [prospectos, setProspectos] = useState([]);
  const [agenciaSel, setAgenciaSel] = useState(null);
  const [prospectoSel, setProspectoSel] = useState(null);
  const [form, setForm] = useState({
    persona_contactada: '', cargo_persona: '',
    canal: 'presencial', resultado: 'positivo',
    duracion_minutos: '',
    objetivo: '', notas: '',
    siguiente_accion: '', siguiente_accion_fecha: '',
    gps_lat: null, gps_lng: null,
    fecha_visita: new Date().toISOString().slice(0, 16),
  });
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Cargar agencias y prospectos asignados al comercial
  useEffect(() => {
    if (!open) return;
    const empresaId = getEmpresaId();
    Promise.all([
      fetch(`${API_BASE}/turismo/agencias?empresa_id=${empresaId}&limit=500`,
        { headers: getAuthHeaders() }).then(r => r.json()),
      fetch(`${API_BASE}/turismo/comercial/prospectos?empresa_id=${empresaId}`,
        { headers: getAuthHeaders() }).then(r => r.json()),
    ]).then(([ag, pr]) => {
      setAgencias(Array.isArray(ag) ? ag : (ag.agencias || ag.items || []));
      setProspectos(pr.prospectos || []);
    }).catch(() => {});
    // Pre-rellenar si se pasó prefill
    if (prefill?.agencia_id) {
      setTarget('agencia');
      setAgenciaSel({ id: prefill.agencia_id, nombre: prefill.agencia_nombre || '' });
    }
    if (prefill?.prospecto_id) {
      setTarget('prospecto');
      setProspectoSel({ id: prefill.prospecto_id, nombre: prefill.prospecto_nombre || '' });
    }
  }, [open, prefill]);

  const handleCapturarGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError('Tu navegador no soporta GPS');
      return;
    }
    setGpsLoading(true);
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({
          ...f,
          gps_lat: pos.coords.latitude,
          gps_lng: pos.coords.longitude,
        }));
        setGpsLoading(false);
      },
      (err) => {
        setGpsError(err.message || 'No se pudo obtener la ubicación');
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const handleGuardar = async () => {
    setError(null);
    if (target === 'agencia' && !agenciaSel) { setError('Selecciona una agencia'); return; }
    if (target === 'prospecto' && !prospectoSel) { setError('Selecciona un prospecto'); return; }

    setSaving(true);
    try {
      const body = {
        empresa_id: getEmpresaId(),
        agencia_id: target === 'agencia' ? agenciaSel.id : null,
        prospecto_id: target === 'prospecto' ? prospectoSel.id : null,
        persona_contactada: form.persona_contactada || null,
        cargo_persona: form.cargo_persona || null,
        canal: form.canal,
        resultado: form.resultado,
        duracion_minutos: form.duracion_minutos ? Number(form.duracion_minutos) : null,
        objetivo: form.objetivo || null,
        notas: form.notas || null,
        siguiente_accion: form.siguiente_accion || null,
        siguiente_accion_fecha: form.siguiente_accion_fecha || null,
        gps_lat: form.gps_lat,
        gps_lng: form.gps_lng,
        fecha_visita: form.fecha_visita ? new Date(form.fecha_visita).toISOString() : null,
      };
      const r = await fetch(`${API_BASE}/turismo/comercial/visitas`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Error guardando');
      }
      const v = await r.json();
      onSaved?.(v);
      onClose();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  return (
    <SlideOver open={open} onClose={onClose} variant="wide"
      title="Nueva visita"
      subtitle="Registra el contacto con una agencia o prospecto"
      icon={<LocationOnIcon />}
      footer={
        <>
          <Button onClick={onClose} startIcon={<CloseIcon />} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button variant="contained" disableElevation onClick={handleGuardar}
            disabled={saving} startIcon={<SaveIcon />}
            sx={{ textTransform: 'none',
                  background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
            {saving ? 'Guardando…' : 'Guardar visita'}
          </Button>
        </>
      }>
      <Stack spacing={2}>
        {/* Tipo de target */}
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b',
                              textTransform: 'uppercase', mb: 0.75 }}>
            ¿A quién visitaste?
          </Typography>
          <Stack direction="row" spacing={1}>
            {['agencia', 'prospecto'].map(t => (
              <Chip key={t}
                label={t === 'agencia' ? 'Agencia existente' : 'Prospecto nuevo'}
                onClick={() => setTarget(t)}
                sx={{
                  bgcolor: target === t ? '#0d9488' : '#f1f5f9',
                  color: target === t ? '#fff' : '#475569',
                  fontWeight: 700, fontSize: 12,
                }} />
            ))}
          </Stack>
        </Box>

        {target === 'agencia' ? (
          <Autocomplete size="small" options={agencias}
            value={agenciaSel}
            onChange={(_, v) => setAgenciaSel(v)}
            getOptionLabel={(o) => o?.nombre || ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderInput={(p) => <TextField {...p} label="Agencia" />} />
        ) : (
          <Autocomplete size="small" options={prospectos}
            value={prospectoSel}
            onChange={(_, v) => setProspectoSel(v)}
            getOptionLabel={(o) => o?.nombre || ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderInput={(p) => <TextField {...p} label="Prospecto" />} />
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField size="small" fullWidth label="Persona contactada"
            value={form.persona_contactada}
            onChange={e => setForm(f => ({ ...f, persona_contactada: e.target.value }))} />
          <TextField size="small" fullWidth label="Cargo"
            value={form.cargo_persona}
            onChange={e => setForm(f => ({ ...f, cargo_persona: e.target.value }))} />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField select size="small" fullWidth label="Canal" value={form.canal}
            onChange={e => setForm(f => ({ ...f, canal: e.target.value }))}>
            {CANALES_VISITA.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" fullWidth label="Resultado" value={form.resultado}
            onChange={e => setForm(f => ({ ...f, resultado: e.target.value }))}>
            {RESULTADOS_VISITA.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </TextField>
          <TextField size="small" type="number" fullWidth label="Duración (min)"
            value={form.duracion_minutos}
            onChange={e => setForm(f => ({ ...f, duracion_minutos: e.target.value }))} />
        </Stack>

        <TextField size="small" fullWidth type="datetime-local" label="Fecha visita"
          InputLabelProps={{ shrink: true }}
          value={form.fecha_visita}
          onChange={e => setForm(f => ({ ...f, fecha_visita: e.target.value }))} />

        <TextField size="small" fullWidth multiline minRows={2} label="Objetivo"
          placeholder="Ej. Presentar nuevo paquete de Islas del Rosario"
          value={form.objetivo}
          onChange={e => setForm(f => ({ ...f, objetivo: e.target.value }))} />

        <TextField size="small" fullWidth multiline minRows={3} label="Notas de la visita"
          placeholder="Resumen de lo conversado, intereses, objeciones, próximos pasos…"
          value={form.notas}
          onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField size="small" fullWidth label="Siguiente acción"
            placeholder="Ej. Enviar cotización"
            value={form.siguiente_accion}
            onChange={e => setForm(f => ({ ...f, siguiente_accion: e.target.value }))} />
          <TextField size="small" fullWidth type="date" label="Fecha siguiente acción"
            InputLabelProps={{ shrink: true }}
            value={form.siguiente_accion_fecha}
            onChange={e => setForm(f => ({ ...f, siguiente_accion_fecha: e.target.value }))} />
        </Stack>

        {/* GPS */}
        <Box sx={{ border: '1px dashed #cbd5e1', borderRadius: 2, p: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: 12, flex: 1, color: '#475569' }}>
              <LocationOnIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle', color: '#0d9488' }} />
              {form.gps_lat
                ? `Ubicación capturada: ${form.gps_lat.toFixed(5)}, ${form.gps_lng.toFixed(5)}`
                : 'Ubicación no capturada (opcional)'}
            </Typography>
            <Button size="small" disabled={gpsLoading} onClick={handleCapturarGPS}
              startIcon={gpsLoading ? <CircularProgress size={14} /> : <LocationOnIcon />}
              sx={{ textTransform: 'none' }}>
              {form.gps_lat ? 'Re-capturar' : 'Capturar GPS'}
            </Button>
          </Stack>
          {gpsError && (
            <Typography sx={{ fontSize: 11, color: '#dc2626', mt: 0.5 }}>
              {gpsError}
            </Typography>
          )}
        </Box>

        {error && <Typography sx={{ fontSize: 12, color: '#dc2626' }}>{error}</Typography>}
      </Stack>
    </SlideOver>
  );
}
